/**
 * Price Comparison Table — virtualised, horizontally-scrollable table.
 *
 * Renders the main comparison grid using TanStack Table (headless) for
 * column definitions and TanStack Virtual for row virtualisation.
 *
 * Layout:
 *   - Fixed (sticky) columns: SKU, Key, Product Name, Our Price, Min, Max
 *   - Scrollable columns: one per competitor domain
 *
 * The Key column marks the sponsor's key lines (flagged in the master
 * sheet's "Key Lines" column); it is read-only here.
 *
 * Session 5.3: core table + virtualisation + scrolling + colour coding.
 * Session 5.4: sortable headers, staleness indicators, incomplete-data
 *              flag, and integration with FilterPanel.
 *
 * Colour coding (spec §6.8):
 *   - Red text + "-X%" if competitor price is lower than ours
 *   - Green text + "+X%" if competitor price is higher than ours
 *   - "OOS" for out-of-stock competitors
 *   - "N/A" for unavailable price data
 *   - Our price: strikethrough with last known price if we are OOS
 *
 * Spec reference: §6.8 (Frontend: Price Comparison Table Tab).
 */
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { domainName } from '../utils/domains';
import { getScrollPosition, saveScrollPosition } from '../utils/scrollMemory';
import {
  CompetitorPriceCell,
  KeyLineBadge,
  MinMaxPriceCell,
  OurPriceCell,
} from './comparisonCells';
import { parsePrice } from '../utils/priceFormat';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Key under which this table's scroll position is remembered. */
const SCROLL_MEMORY_KEY = 'comparison';

/** Column widths in pixels. */
const COL = {
  sku: 120,
  keyLine: 56,
  productDefault: 500,
  productMin: 160,
  ourPrice: 85,
  min: 90,
  max: 90,
  competitor: 120,
};

/**
 * Compute cumulative left offsets for sticky columns given a product width.
 *
 * @param {number} productW  Current product column width.
 * @returns {Object}         Map of column key to left-offset in pixels.
 */
function stickyOffsets(productW) {
  const product = COL.sku + COL.keyLine;
  return {
    sku: 0,
    keyLine: COL.sku,
    product,
    ourPrice: product + productW,
    min: product + productW + COL.ourPrice,
    max: product + productW + COL.ourPrice + COL.min,
  };
}

/** Row height estimate for the virtualiser (px). */
const ROW_HEIGHT = 48;

/** Overscan rows above/below the visible window. */
const OVERSCAN = 15;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Price Comparison Table with virtualised rows and sticky columns.
 *
 * @param {Object}   props
 * @param {Array}    props.rows              ComparisonRow objects from the API.
 * @param {Array}    props.competitors       Ordered list of competitor domain IDs.
 * @param {number}   props.totalCount        Total matching rows (before limit).
 * @param {string}   [props.sortBy]          Active sort column key.
 * @param {string}   [props.sortDir]         Sort direction: ``"asc"`` or ``"desc"``.
 * @param {Function} [props.onSort]          Callback ``(sortKey) => void``.
 * @param {number}   [props.productWidth]    Current product column width (px).
 * @param {Function} [props.onProductWidthChange] Callback ``(width) => void``.
 * @param {Object}   [props.freshness]       Map of ``domain_id`` to freshness info.
 */
export default function PriceComparisonTable({
  rows,
  competitors,
  totalCount,
  sortBy,
  sortDir,
  onSort,
  freshness,
  filters,
  productWidth: productWidthProp,
  onProductWidthChange,
}) {
  const scrollRef = useRef(null);

  // ------ Resizable product column ------
  // Use prop if provided (lifted state), otherwise fall back to local state
  const [localProductWidth, setLocalProductWidth] = useState(COL.productDefault);
  const productWidth = productWidthProp ?? localProductWidth;
  const setProductWidth = onProductWidthChange ?? setLocalProductWidth;

  const LEFT = useMemo(() => stickyOffsets(productWidth), [productWidth]);

  // Track whether a drag actually happened so we can suppress the click
  // event on the header <th> that would otherwise trigger a sort.
  const didDragRef = useRef(false);

  const handleProductResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = productWidth;
    didDragRef.current = false;

    function onMove(ev) {
      const delta = ev.clientX - startX;
      if (Math.abs(delta) > 2) didDragRef.current = true;
      setProductWidth(Math.max(COL.productMin, startW + delta));
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Suppress the imminent click event on the th if a drag occurred
      if (didDragRef.current) {
        const suppress = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        // Use capture phase to intercept before the th handler fires
        document.addEventListener('click', suppress, { capture: true, once: true });
      }
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [productWidth, setProductWidth]);

  /**
   * Double-click the resize handle to auto-fit the product column to the
   * longest product name (similar to Excel column auto-fit).
   */
  const handleProductAutoFit = useCallback(() => {
    if (!rows || rows.length === 0) return;
    // Measure text widths using a temporary canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    // Match the table body font: 14px (text-sm) system font
    ctx.font = '14px ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"';
    let maxTextWidth = 0;
    for (const row of rows) {
      const name = row.product_name || row.original?.product_name || '';
      const w = ctx.measureText(name).width;
      if (w > maxTextWidth) maxTextWidth = w;
    }
    // Add padding: 12px left padding (px-3) + ~40px for link padding/icon + 12px right
    const newWidth = Math.max(COL.productMin, Math.ceil(maxTextWidth + 64));
    setProductWidth(newWidth);
  }, [rows, setProductWidth]);

  // When stock filters are active, hide competitor columns with no qualifying data
  const visibleCompetitors = useMemo(() => {
    if (!filters?.competitor_has_price && !filters?.competitor_in_stock) {
      return competitors;
    }
    return competitors.filter((domainId) =>
      rows.some((row) => {
        const ps = row.competitor_prices?.[domainId];
        if (!ps) return false;
        if (filters.competitor_has_price && (ps.status !== 'available' || ps.value == null)) {
          return false;
        }
        if (filters.competitor_in_stock && ps.status !== 'available') {
          return false;
        }
        return true;
      }),
    );
  }, [competitors, rows, filters]);

  // ------ Column definitions ------
  const columns = useMemo(() => {
    /** Sticky column helper. */
    const sticky = (left, isLast = false) => ({
      sticky: true,
      stickyLeft: left,
      isLastSticky: isLast,
    });

    const fixed = [
      {
        accessorKey: 'our_sku',
        header: 'SKU',
        size: COL.sku,
        meta: { ...sticky(LEFT.sku), sortKey: 'our_sku' },
      },
      {
        id: 'key_line',
        header: 'Key',
        size: COL.keyLine,
        meta: { ...sticky(LEFT.keyLine) },
        cell: ({ row }) => (row.original.key_line ? <KeyLineBadge /> : null),
      },
      {
        accessorKey: 'product_name',
        header: 'Product',
        size: productWidth,
        meta: { ...sticky(LEFT.product), sortKey: 'product_name', resizable: true },
        cell: ({ row }) => {
          const incomplete = row.original._incomplete;
          return (
            <div className="flex items-center gap-1.5">
              <Link
                to={`/trends/${encodeURIComponent(row.original.our_sku)}`}
                className="text-blue-600 hover:text-blue-800 hover:underline
                           truncate block"
                style={{ maxWidth: productWidth - 40 }}
                title={row.original.product_name}
              >
                {row.original.product_name}
              </Link>
              {incomplete && (
                <span
                  className="flex-shrink-0 inline-block h-2 w-2 rounded-full
                             bg-amber-400"
                  title="Incomplete data — not all competitors have prices"
                />
              )}
            </div>
          );
        },
      },
      {
        id: 'our_price',
        header: 'Our Price',
        size: COL.ourPrice,
        meta: { ...sticky(LEFT.ourPrice), sortKey: 'our_price' },
        cell: (info) => <OurPriceCell row={info.row} />,
      },
      {
        id: 'min_competitor',
        header: 'Min',
        size: COL.min,
        meta: { ...sticky(LEFT.min), sortKey: 'min_competitor' },
        cell: ({ row }) => (
          <MinMaxPriceCell
            priceStatus={row.original.min_competitor}
            diffPct={row.original.min_competitor_diff_pct}
            url={row.original.min_competitor_url}
          />
        ),
      },
      {
        id: 'max_competitor',
        header: 'Max',
        size: COL.max,
        meta: { ...sticky(LEFT.max, true), sortKey: 'max_competitor' },
        cell: ({ row }) => (
          <MinMaxPriceCell
            priceStatus={row.original.max_competitor}
            diffPct={row.original.max_competitor_diff_pct}
            url={row.original.max_competitor_url}
          />
        ),
      },
    ];

    // One column per visible competitor
    const compCols = visibleCompetitors.map((domainId) => {
      return {
        id: `comp_${domainId}`,
        header: () => (
          <span className="text-[10px] leading-tight break-words whitespace-normal">
            {domainName(domainId)}
          </span>
        ),
        size: COL.competitor,
        meta: { domainId },
        cell: ({ row }) => {
          const ourVal = parsePrice(row.original.our_price?.value);
          return (
            <CompetitorPriceCell
              priceStatus={row.original.competitor_prices?.[domainId]}
              ourPriceValue={
                row.original.our_price?.status === 'available' ? ourVal : null
              }
              url={row.original.competitor_urls?.[domainId]}
            />
          );
        },
      };
    });

    return [...fixed, ...compCols];
  }, [visibleCompetitors, productWidth, LEFT]);

  // ------ TanStack Table ------
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows: tableRows } = table.getRowModel();

  // ------ Row virtualiser ------
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  // ------ Scroll position retention (survives tab navigation) ------
  // Set true once the restore effect below has run (or found nothing to
  // restore). The cleanup save is gated on it: React StrictMode runs a
  // synthetic mount->cleanup->remount cycle in dev, and an ungated
  // cleanup save would clobber the stored offsets with the fresh
  // mount's zero position before the restore ever reads them.
  const restoredRef = useRef(false);

  // Save on every scroll event (a Map write — no throttling needed) and
  // again on unmount, while the node is still attached (layout cleanups
  // run before React detaches the DOM; a detached node reads 0, hence
  // the isConnected guard).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const save = () => {
      if (!el.isConnected) return;
      saveScrollPosition(SCROLL_MEMORY_KEY, {
        top: el.scrollTop,
        left: el.scrollLeft,
      });
    };
    el.addEventListener('scroll', save, { passive: true });
    return () => {
      el.removeEventListener('scroll', save);
      if (restoredRef.current) save();
    };
  }, []);

  const virtualItems = virtualizer.getVirtualItems();

  // Restore once per mount, after the virtualiser has produced items —
  // before that the table body has no height and the browser would clamp
  // any restored offset back to 0. The once-only guard stops the
  // stale-while-revalidate data swap from re-triggering the restore.
  useLayoutEffect(() => {
    if (restoredRef.current) return;
    if (tableRows.length === 0 || virtualItems.length === 0) return;
    const el = scrollRef.current;
    if (!el) return;
    restoredRef.current = true;
    const saved = getScrollPosition(SCROLL_MEMORY_KEY);
    if (saved) {
      el.scrollTop = saved.top;
      el.scrollLeft = saved.left;
    }
  }, [tableRows.length, virtualItems.length]);
  const paddingTop =
    virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? virtualizer.getTotalSize() -
        virtualItems[virtualItems.length - 1].end
      : 0;

  const colCount = columns.length;

  // Calculate the actual total width of all columns so the table
  // never auto-stretches wider than its content. This is what enables
  // horizontal scrolling when columns exceed the viewport width.
  const totalTableWidth = useMemo(() => {
    const stickyWidth =
      COL.sku + COL.keyLine + productWidth + COL.ourPrice + COL.min + COL.max;
    const compWidth = visibleCompetitors.length * COL.competitor;
    return stickyWidth + compWidth;
  }, [productWidth, visibleCompetitors.length]);

  // ------ Render ------
  return (
    <div className="flex flex-col flex-1 min-h-0 min-w-0">
      {/* Status bar */}
      <div className="flex items-center justify-between px-1 py-2 text-sm text-gray-500">
        <span>
          Showing{' '}
          <span className="font-medium text-gray-700">{rows.length}</span>
          {totalCount !== rows.length && (
            <>
              {' '}of{' '}
              <span className="font-medium text-gray-700">{totalCount}</span>
            </>
          )}
          {' '}products
        </span>
      </div>

      {/* Scrollable table container */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto border border-gray-200
                   rounded-lg bg-white shadow-sm"
      >
        <table className="border-collapse" style={{ minWidth: totalTableWidth }}>
          {/* ---------- Header ---------- */}
          <thead className="sticky top-0 z-20">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  const m = header.column.columnDef.meta ?? {};
                  const isSticky = m.sticky;
                  const isLastSticky = m.isLastSticky;
                  const canSort = !!m.sortKey && !!onSort;
                  const isSorted = m.sortKey && sortBy === m.sortKey;
                  const staleInfo =
                    m.domainId && freshness?.[m.domainId];

                  return (
                    <th
                      key={header.id}
                      onClick={
                        canSort ? () => onSort(m.sortKey) : undefined
                      }
                      className={[
                        'px-3 py-2.5 text-left text-xs font-semibold',
                        'text-gray-500 uppercase tracking-wider',
                        'border-b border-gray-200 bg-gray-50',
                        isSticky ? 'sticky z-30' : '',
                        isLastSticky
                          ? 'border-r-2 border-r-gray-300'
                          : '',
                        canSort
                          ? 'cursor-pointer select-none hover:bg-gray-100'
                          : '',
                        m.resizable ? 'relative' : '',
                      ].join(' ')}
                      style={{
                        width: header.getSize(),
                        minWidth: header.getSize(),
                        ...(isSticky ? { left: m.stickyLeft } : {}),
                      }}
                    >
                      <div className={[
                        'flex gap-1',
                        m.domainId ? 'items-start' : 'items-center',
                      ].join(' ')}>
                        <span className={m.domainId ? 'flex-1 min-w-0' : ''}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>

                        {/* Sort direction indicator */}
                        {isSorted && (
                          <span className="text-blue-600 text-[10px] flex-shrink-0">
                            {sortDir === 'asc' ? '\u25B2' : '\u25BC'}
                          </span>
                        )}

                        {/* Staleness indicator */}
                        {staleInfo?.is_stale && (
                          <span
                            className="flex-shrink-0 inline-block h-2 w-2
                                       rounded-full bg-amber-400 mt-0.5"
                            title={`${domainName(m.domainId)}: data may be stale`}
                          />
                        )}
                      </div>

                      {/* Resize handle for Product column */}
                      {m.resizable && (
                        <div
                          onMouseDown={handleProductResize}
                          onDoubleClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            handleProductAutoFit();
                          }}
                          className="absolute top-0 right-0 w-1.5 h-full
                                     cursor-col-resize hover:bg-blue-400
                                     active:bg-blue-500 transition-colors"
                          title="Drag to resize — double-click to auto-fit"
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          {/* ---------- Body ---------- */}
          <tbody>
            {/* Top spacer for virtualisation */}
            {paddingTop > 0 && (
              <tr>
                <td
                  colSpan={colCount}
                  style={{ height: paddingTop, padding: 0, border: 'none' }}
                />
              </tr>
            )}

            {virtualItems.map((vRow) => {
              const row = tableRows[vRow.index];
              return (
                <tr
                  key={row.id}
                  className="group border-b border-gray-100 transition-colors
                             bg-white hover:bg-blue-50"
                >
                  {row.getVisibleCells().map((cell) => {
                    const m = cell.column.columnDef.meta ?? {};
                    const isSticky = m.sticky;
                    const isLastSticky = m.isLastSticky;

                    return (
                      <td
                        key={cell.id}
                        className={[
                          'px-3 py-2 text-sm whitespace-nowrap',
                          isSticky
                            ? 'sticky z-10 bg-inherit'
                            : '',
                          isLastSticky
                            ? 'border-r-2 border-r-gray-300'
                            : '',
                        ].join(' ')}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          ...(isSticky ? { left: m.stickyLeft } : {}),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {/* Bottom spacer for virtualisation */}
            {paddingBottom > 0 && (
              <tr>
                <td
                  colSpan={colCount}
                  style={{ height: paddingBottom, padding: 0, border: 'none' }}
                />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
